import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';

import Color from '@tool/color';
import { init } from 'echarts';
import { isEmpty, isNumber, round } from 'lodash';
import moDemandPdf from '../store';
import moIndex from '../../store';

interface IProps {}
interface IState {}
let chart: any = null;

const QuantityChart = (props: IProps, state: IState) => {
  const navi = useNavigate();
  const quantityRef = useRef<any>(null);
  const { isMock } = moIndex;
  const { Xvlaue, Yvlaue, hotListTop30 } = moDemandPdf;

  const enums: any = {
    axis_amount: '招聘量',
    axis_amount_change: '招聘量涨幅',
    axis_salary: '招聘薪酬',
    axis_salary_change: '招聘薪酬涨幅',
  };

  const nameEnum: any = {
    招聘量: ['需求量大', '需求量较小'],
    招聘量涨幅: ['需求增长快', '需求增长慢'],
    招聘薪酬: ['薪酬高', '薪酬较低'],
    招聘薪酬涨幅: ['薪酬增长快', '薪酬增长慢'],
  };


  const getName = useMemo(() => {
    let name1: any = '';
    let name2: any = '';
    let name3: any = '';
    let name4: any = '';
    if (Xvlaue && Yvlaue) {
      name1 = `${nameEnum[enums[Xvlaue]][1]}，${nameEnum[enums[Yvlaue]][0]}`;
      name2 = `${nameEnum[enums[Xvlaue]][0]}，${nameEnum[enums[Yvlaue]][0]}`;
      name3 = `${nameEnum[enums[Xvlaue]][1]}，${nameEnum[enums[Yvlaue]][1]}`;
      name4 = `${nameEnum[enums[Xvlaue]][0]}，${nameEnum[enums[Yvlaue]][1]}`;
    }

    return { name1, name2, name3, name4 };
  }, [Xvlaue, Yvlaue]);

  const getResult = useMemo(() => {
    let xMin: any = 0;
    let yMin: any = 0;
    let xMax: any = 0;
    let yMax: any = 0;
    const list: any = [];
    let xRatio: any = 0;
    let yRatio: any = 0;
    let xMid: any = 0;
    let yMid: any = 0;
    if (!isEmpty(hotListTop30)) {
      if (Xvlaue === 'axis_amount_change' || Xvlaue === 'axis_salary_change') {
        xMin = round(hotListTop30[Xvlaue].min * 100, 1);
        xMax = round(hotListTop30[Xvlaue].max * 100, 1);
        xMid = round(hotListTop30[Xvlaue].percent_50 * 100, 1);
      } else {
        xMin = hotListTop30[Xvlaue].min;
        xMax = hotListTop30[Xvlaue].max;
        xMid = hotListTop30[Xvlaue].percent_50;
      }
      if (Yvlaue === 'axis_amount_change' || Yvlaue === 'axis_salary_change') {
        yMin = round(hotListTop30[Yvlaue].min * 100, 1);
        yMax = round(hotListTop30[Yvlaue].max * 100, 1);
        yMid = round(hotListTop30[Yvlaue].percent_50 * 100, 1);
      } else {
        yMin = hotListTop30[Yvlaue].min;
        yMax = hotListTop30[Yvlaue].max;

        yMid = hotListTop30[Yvlaue].percent_50;
      }
      if (hotListTop30[Yvlaue].max <= 0) {
        yRatio = round(
          ((Math.abs(hotListTop30[Yvlaue].percent_50) -
            Math.abs(hotListTop30[Yvlaue].max)) /
            (Math.abs(hotListTop30[Yvlaue].min) -
              Math.abs(hotListTop30[Yvlaue].max))) *
            91,
          1,
        );
      } else {
        yRatio = round(
          ((hotListTop30[Yvlaue].max - hotListTop30[Yvlaue].percent_50) /
            (hotListTop30[Yvlaue].max - hotListTop30[Yvlaue].min)) *
            91,
          1,
        );
      }
      xRatio = round(
        ((hotListTop30[Xvlaue].percent_50 - hotListTop30[Xvlaue].min) /
          (hotListTop30[Xvlaue].max - hotListTop30[Xvlaue].min)) *
          94,
        1,
      );
      const points = hotListTop30.category_points;
      if (!Array.isArray(points)) {
        console.error('[QuantityChart] category_points 非数组', { category_points: points, hotListTop30 });
      } else {
        points.forEach((res: any) => {
          const item: any = [];
          const emen: any = {
            axis_amount_change: 'amount_change',
            axis_salary_change: 'salary_change',
            axis_salary: 'salary',
            axis_amount: 'amount',
          };
          if (
            Xvlaue === 'axis_amount_change' ||
            Xvlaue === 'axis_salary_change'
          ) {
            item[0] = round(res[emen[Xvlaue]] * 100, 1);
          } else {
            item[0] = res[emen[Xvlaue]];
          }
          if (
            Yvlaue === 'axis_amount_change' ||
            Yvlaue === 'axis_salary_change'
          ) {
            item[1] = round(res[emen[Yvlaue]] * 100, 1);
          } else {
            item[1] = res[emen[Yvlaue]];
          }
          item[2] = res.category_3;
          list.push(item);
        });
      }
    }
    return { xMin, yMin, xMax, yMax, list, xRatio, yRatio, xMid, yMid };
  }, [Xvlaue, Yvlaue, hotListTop30]);

  const colorList = [Color.primary, Color.secondary];

  const render = () => {
    if (chart) chart.dispose();
    chart = init(quantityRef.current);
    const option = {
      grid: {
        left: '8%',
        right: '2%',
        bottom: '6%',
        top: '3%',
      },
      legend: {
        show: false,
      },
      xAxis: [
        {
          type: 'value',
          scale: true,
          min: getResult.xMin,
          max: getResult.xMax,
          axisTick: { show: false },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: Color.ccc },
          },
          axisLabel: {
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${
                Xvlaue === 'axis_amount_change' ||
                Xvlaue === 'axis_salary_change' ?
                  val + '%' :
                  Number(val).toLocaleString()
              }`;
            },
            color: Color.blackColor(0.5),
            fontFamily: 'Roboto',
            showMinLabel: false,
            showMaxLabel: false,
            fontSize:10,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          scale: true,
          min: getResult.yMin,
          max: getResult.yMax,
          axisTick: { show: false },
          axisLine: {
            show: true,
            onZero: false,
            lineStyle: { color: Color.ccc },
          },
          axisLabel: {
            formatter: (val: number) => {
              if (val === 0) return 0;
              return `${
                Yvlaue === 'axis_amount_change' ||
                Yvlaue === 'axis_salary_change' ?
                  val + '%' :
                  Number(val).toLocaleString()
              }`;
            },
            color: Color.blackColor(0.5),
            fontSize:10,
            fontFamily: 'Roboto',
            showMinLabel: false,
            showMaxLabel: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: [
        {
          name: 'Male',
          type: 'scatter',
          emphasis: {
            focus: 'series',
          },
          symbolSize: 1,
          label: {
            show: true,
            position: [-12, -10],
            backgroundColor: Color.blackColor(0.7),
            padding: [3, 8],
            color: Color.white,
            borderWidth: 0,
            borderRadius: 14,
            formatter: (parmes: any) => {
              const list: any = ['{a|}'];
              const str = `{a|}{b|${parmes.data[2]}}`;
              for (let i = 0; i < parmes.data[2].length; i++) {
                list.push(`{b|${parmes.data[2][i]}}`);
              }

              return parmes.data[0] > getResult.xMax * 0.95 ?
                list.join('\n') :
                str;
            },

            rich: {
              a: {
                backgroundColor: Color.white,
                width: 8,
                height: 8,
                borderRadius: 4,
              },
              b: {
                padding: [0, 0, 0, 1],
                color: Color.white,
                fontSize: 10,
                lineHeight: 14,
              },
            },
          },
          itemStyle: {
            color: Color.blackColor(0.3),
          },
          // prettier-ignore
          data: getResult.list,
          markArea: {
            silent: true,
            label: {
              show: true,
            },
            data: [
              [
                {
                  name: getName.name1,
                  x: '8%',
                  y: '3%',
                  label: {
                    position: 'inside',
                    fontSize: 20,
                    fontWeight: 600,
                    color: Color.blackColor(0.1),
                  },
                  itemStyle: {
                    color: Color.errorColor(0.1),
                  },
                },
                {
                  x: `${getResult.xRatio + 4}%`,
                  y: `${getResult.yRatio + 3}%`,
                },
              ],
              [
                {
                  name: getName.name2,
                  x: `${getResult.xRatio + 4}%`,
                  y: '3%',
                  itemStyle: {
                    color: Color.errorColor(0.3),
                  },
                  label: {
                    position: 'inside',
                    fontSize: 20,
                    fontWeight: 600,
                    color: Color.blackColor(0.1),
                  },
                },
                {
                  x: '98%',
                  y: `${getResult.yRatio + 3}%`,
                },
              ],
              [
                {
                  name: getName.name3,
                  x: '8%',
                  y: `${getResult.yRatio + 3}%`,
                  itemStyle: {
                    color: Color.errorColor(0.05),
                  },
                  label: {
                    position: 'inside',
                    fontSize: 20,
                    fontWeight: 600,
                    color: Color.blackColor(0.1),
                  },
                },
                {
                  x: `${getResult.xRatio + 4}%`,
                  y: '94%',
                },
              ],
              [
                {
                  name: getName.name4,
                  x: `${getResult.xRatio + 4}%`,
                  y: `${getResult.yRatio + 3}%`,
                  itemStyle: {
                    color: Color.errorColor(0.15),
                  },
                  label: {
                    position: 'inside',
                    fontSize: 20,
                    fontWeight: 600,
                    color: Color.blackColor(0.1),
                  },
                },
                {
                  x: '98%',
                  y: '94%',
                },
              ],
            ],
          },
        },
      ],
    };
    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener('resize', render);
    return () => {
      window.removeEventListener('resize', render);
    };
  }, [getResult, getName, Xvlaue, Yvlaue]);

  return (
    <section className="quantity-svg-warp">
      <section className="chart-top">
        <p className="chart-left-title">{enums[Yvlaue]}</p>
        <section
          ref={quantityRef}
          className="quantity-chart"
          style={{
            backgroundImage: isMock ?
              'url(https://file.smartsalary.cn/web-img%402.0/market/image/ismock.png)' :
              '',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
          }}></section>
      </section>
      <p className="chart-bot">{enums[Xvlaue]}</p>
    </section>
  );
};

export default observer(QuantityChart);
